import React from "react";

import { mdiDownloadBox, mdiPoll, mdiSquareRounded } from "@mdi/js";
import Icon, { Stack } from "@mdi/react";
import axios from "axios";
import { saveAs } from "file-saver";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { NavLink, useParams } from "react-router-dom";

import { MENU_ICON_SIZE } from "./Menu";
import MenuItem from "./MenuItem";
import SubMenu from "./SubMenu";
import { TypeEnum } from "../../api/ProjectAPI.types";
import { ANALYST } from "../../constants/roles";
import useAbility from "../../hooks/useAbility";
import { formatHeaders } from "../../utils/Api";
import rootURL from "../../utils/RootURL";
import { PROJECTS_ROUTE } from "../routes/routes";

import type { JSX } from "react";

import type { AppState } from "../../reducers";

function AnalyticsSubMenu(): JSX.Element {
  const { t } = useTranslation("editor-menu");
  const { projectSlug } = useParams<{ projectSlug: string }>();
  const { hasRole, canAccess } = useAbility();

  const token = useSelector((state: AppState) => state.loginReducer.token);

  const currentProject = useSelector(
    (state: AppState) => state.architectureReducer.project,
  );

  const orgaConfig = useSelector(
    (state: AppState) => state.fetchReducer.orga_config,
  );

  const { project: canAccessProject } = canAccess(
    orgaConfig.data?.organization_options,
  );

  const isEDU = currentProject?.type === TypeEnum.Edu;

  const exportSponsoring = (): void => {
    axios({
      url: rootURL(
        `/api/editor_stats/v1/admin/content/preroll_views?period_start=1970-01-01&export`,
      ),
      method: "GET",
      headers: formatHeaders(token, [
        {
          key: "xx-ok-project",
          value: projectSlug,
        },
      ]),
    }).then((res) => {
      const csv = new File([res.data as BlobPart], "preroll_views.csv", {
        type: "text/csv",
      });
      saveAs(csv);
    });
  };

  const exportDisplay = (): void => {
    axios({
      url: rootURL(
        `/api/editor_stats/v1/admin/display/views?period_start=1970-01-01&export`,
      ),
      method: "GET",
      headers: formatHeaders(token, [
        {
          key: "xx-ok-project",
          value: projectSlug,
        },
      ]),
    }).then((res) => {
      const csv = new File([res.data as BlobPart], "preroll_views.csv", {
        type: "text/csv",
      });
      saveAs(csv);
    });
  };

  return (
    <SubMenu
      label={t("analytics")}
      icon={
        <Stack size={MENU_ICON_SIZE}>
          <Icon path={mdiSquareRounded} />
          <Icon className="text-card" path={mdiPoll} size={0.5} />
        </Stack>
      }
    >
      {canAccessProject.contentsAnalytics ? (
        <MenuItem
          label={t("content_analytics")}
          component={
            <NavLink
              to={`${PROJECTS_ROUTE}/${projectSlug}/analytics/content`}
            />
          }
        />
      ) : null}
      {canAccessProject.offersAnalytics ? (
        <MenuItem
          label={t("offer_analytics")}
          component={
            <NavLink to={`${PROJECTS_ROUTE}/${projectSlug}/analytics/offer`} />
          }
        />
      ) : null}
      {canAccessProject.transactionsAnalytics ? (
        <MenuItem
          label={t("transactions_analytics")}
          component={
            <NavLink
              to={`${PROJECTS_ROUTE}/${projectSlug}/analytics/transactions`}
            />
          }
        />
      ) : null}
      {canAccessProject.advertisingsSponsoringsAnalytics ? (
        <MenuItem
          component={<div onClick={exportSponsoring} />}
          label={t("advertising-csai-mp4")}
          suffix={<Icon path={mdiDownloadBox} size={0.875} />}
        />
      ) : null}
      {canAccessProject.displaysAnalytics ? (
        <MenuItem
          component={<div onClick={exportDisplay} />}
          label={t("display")}
          suffix={<Icon path={mdiDownloadBox} size={0.875} />}
        />
      ) : null}
      {canAccessProject.usersAnalytics && !isEDU && !hasRole([ANALYST]) ? (
        <MenuItem
          label={t("users")}
          component={
            <NavLink to={`${PROJECTS_ROUTE}/${projectSlug}/analytics/users`} />
          }
        />
      ) : null}
    </SubMenu>
  );
}

export default AnalyticsSubMenu;
